import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  EditOutlined as EditOutlinedIcon,
  ExpandMore as ExpandMoreIcon,
  FolderOpen as FolderOpenIcon,
  Refresh as RefreshIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
} from '@mui/icons-material';
import {
  api,
  AllProjectVerificationRow,
  MyProjectVerificationRow,
  ProjectResourceLink,
  ProjectVerificationRecord,
  ResponsiblePartyRow,
} from '../../services/api';
import {
  isDuplicateProjectResourceLink,
  RESOURCE_LINK_DUPLICATE_INFO,
} from '../../utils/projectResourceLinkDuplicate';

type TabValue = 'mine' | 'all' | 'parties';

interface ProjectVerificationDialogProps {
  open: boolean;
  onClose: () => void;
  isAdmin?: boolean;
  initialTab?: TabValue;
}

interface DraftState {
  siteMap: boolean;
  units: boolean;
  priceList: boolean;
  takenAction: boolean;
  projectEssential: boolean;
  agentAccessFolderLink: string;
  remarks: string;
}

type AdminRemarkDrafts = Record<number, string>;

interface AdminRemarkEditorState {
  verificationId: number;
  projectName: string;
  verifierName: string;
  originalRemark: string | null;
}

function emptyDraft(agentAccessFolderLink = ''): DraftState {
  return {
    siteMap: false,
    units: false,
    priceList: false,
    takenAction: false,
    projectEssential: false,
    agentAccessFolderLink,
    remarks: '',
  };
}

function draftFromRecord(
  record: ProjectVerificationRecord | null,
  fallbackAgentAccessFolderLink = ''
): DraftState {
  if (!record) return emptyDraft(fallbackAgentAccessFolderLink);
  return {
    siteMap: record.siteMapVerified === true,
    units: record.unitsVerified === true,
    priceList: record.priceListVerified === true,
    takenAction: record.takenAction === true,
    projectEssential: record.projectEssentialVerified === true,
    agentAccessFolderLink: record.agentAccessFolderLink || fallbackAgentAccessFolderLink,
    remarks: record.remarks || '',
  };
}

function formatDateTime(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
}

function VerificationStatusIcon({
  value,
  size = 'medium',
}: {
  value: boolean | null;
  size?: 'small' | 'medium';
}): React.ReactElement {
  const iconSize = size === 'small' ? 16 : 22;
  if (value === true) {
    return (
      <CheckCircleIcon
        sx={{ fontSize: iconSize, color: 'var(--color-success)', verticalAlign: 'middle' }}
        titleAccess="Yes"
      />
    );
  }
  if (value === false) {
    return (
      <CancelIcon
        sx={{ fontSize: iconSize, color: 'var(--color-danger)', verticalAlign: 'middle' }}
        titleAccess="No"
      />
    );
  }
  return (
    <RadioButtonUncheckedIcon
      sx={{
        fontSize: size === 'small' ? 14 : 20,
        color: 'rgba(var(--color-text-rgb), 0.35)',
        verticalAlign: 'middle',
      }}
      titleAccess="Not set"
    />
  );
}

/** The five verification items in display order, so history lines can't drift from the form. */
const VERIFICATION_ITEMS: Array<{
  label: string;
  read: (record: ProjectVerificationRecord) => boolean | null;
}> = [
  { label: 'Site Map', read: (r) => r.siteMapVerified },
  { label: 'Units', read: (r) => r.unitsVerified },
  { label: 'Price List', read: (r) => r.priceListVerified },
  { label: 'Taken Action', read: (r) => r.takenAction },
  { label: 'Project Essentials', read: (r) => r.projectEssentialVerified },
];

/** Status first, label second — the tick reads before the thing it applies to. */
function StatusPill({ value, label }: { value: boolean | null; label: string }): React.ReactElement {
  const done = value === true;
  const failed = value === false;
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        pl: 0.5,
        pr: 0.9,
        py: 0.3,
        borderRadius: 999,
        border: '1px solid',
        borderColor: done
          ? 'rgba(var(--color-success-rgb), 0.45)'
          : failed
          ? 'rgba(var(--color-danger-rgb), 0.45)'
          : 'rgba(var(--color-border-rgb), 0.22)',
        bgcolor: done
          ? 'var(--color-success-soft)'
          : failed
          ? 'var(--color-danger-soft)'
          : 'transparent',
      }}
    >
      <VerificationStatusIcon value={value} size="small" />
      <Typography
        variant="caption"
        sx={{ fontWeight: 600, whiteSpace: 'nowrap', color: 'var(--color-text-strong)' }}
      >
        {label}
      </Typography>
    </Box>
  );
}

type AllProjectsSortKey =
  | 'siteMap'
  | 'units'
  | 'priceList'
  | 'takenAction'
  | 'projectEssential'
  | 'verifiedAt';

type AllProjectsFlatRow =
  | { kind: 'empty'; project: AllProjectVerificationRow }
  | { kind: 'verification'; project: AllProjectVerificationRow; verification: ProjectVerificationRecord };

function buildAllProjectsFlatRows(rows: AllProjectVerificationRow[]): AllProjectsFlatRow[] {
  const out: AllProjectsFlatRow[] = [];
  for (const project of rows) {
    if (project.verifications.length === 0) {
      out.push({ kind: 'empty', project });
    } else {
      for (const verification of project.verifications) {
        out.push({ kind: 'verification', project, verification });
      }
    }
  }
  return out;
}

function sortPrimaryValue(
  row: AllProjectsFlatRow,
  key: AllProjectsSortKey
): number | null {
  if (row.kind === 'empty') return null;
  const v = row.verification;
  switch (key) {
    case 'siteMap':
    case 'units':
    case 'priceList':
    case 'takenAction':
    case 'projectEssential': {
      const field =
        key === 'siteMap'
          ? v.siteMapVerified
          : key === 'units'
          ? v.unitsVerified
          : key === 'priceList'
          ? v.priceListVerified
          : key === 'takenAction'
          ? v.takenAction
          : v.projectEssentialVerified;
      if (field === true) return 2;
      if (field === false) return 1;
      return 0;
    }
    case 'verifiedAt': {
      if (!v.verifiedAt) return 0;
      const t = new Date(v.verifiedAt).getTime();
      return Number.isNaN(t) ? 0 : t;
    }
    default:
      return 0;
  }
}

function compareAllProjectsFlatRows(
  a: AllProjectsFlatRow,
  b: AllProjectsFlatRow,
  orderBy: AllProjectsSortKey,
  order: 'asc' | 'desc'
): number {
  const dir = order === 'asc' ? 1 : -1;
  const aPri = sortPrimaryValue(a, orderBy);
  const bPri = sortPrimaryValue(b, orderBy);
  const aEmpty = a.kind === 'empty';
  const bEmpty = b.kind === 'empty';

  if (!aEmpty && !bEmpty && aPri !== null && bPri !== null && aPri !== bPri) {
    return (aPri < bPri ? -1 : 1) * dir;
  }
  if (aEmpty !== bEmpty) {
    return aEmpty ? 1 : -1;
  }
  if (aEmpty && bEmpty) {
    const nameA = a.project.projectName || a.project.projectId;
    const nameB = b.project.projectName || b.project.projectId;
    return nameA.localeCompare(nameB);
  }
  const nameA = a.project.projectName || a.project.projectId;
  const nameB = b.project.projectName || b.project.projectId;
  if (nameA !== nameB) return nameA.localeCompare(nameB);
  if (a.kind !== 'verification' || b.kind !== 'verification') return 0;
  const ua = a.verification.userName || a.verification.ceaNumber || a.verification.userId;
  const ub = b.verification.userName || b.verification.ceaNumber || b.verification.userId;
  return ua.localeCompare(ub);
}

function normalizeOptionalText(value: string | null | undefined): string {
  return (value || '').trim();
}

function isValidAgentAccessFolderLink(value: string): boolean {
  return /^https?:\/\//i.test(value.trim());
}

function ResourceLinkDuplicateAlert() {
  return (
    <Alert severity="info">
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {RESOURCE_LINK_DUPLICATE_INFO.title}
      </Typography>
      {RESOURCE_LINK_DUPLICATE_INFO.message}
    </Alert>
  );
}

const stickyTableContainerSx = {
  maxHeight: 'min(52vh, 560px)',
  overflow: 'auto',
} as const;

const stickyHeadCellSx = {
  /** Opaque thead color so scrolling body rows never show through the sticky header */
  backgroundColor: 'var(--color-surface-thead)',
  color: 'var(--color-text-strong)',
  fontWeight: 600,
  whiteSpace: 'nowrap' as const,
  zIndex: 3,
  boxShadow: 'inset 0 -1px 0 rgba(var(--color-border-rgb), 0.12)',
};

const ProjectVerificationDialog: React.FC<ProjectVerificationDialogProps> = ({
  open,
  onClose,
  isAdmin = false,
  initialTab = 'mine',
}) => {
  const [tab, setTab] = useState<TabValue>(initialTab);
  const [loading, setLoading] = useState(false);
  const [submittingProjectId, setSubmittingProjectId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [myRows, setMyRows] = useState<MyProjectVerificationRow[]>([]);
  const [allRows, setAllRows] = useState<AllProjectVerificationRow[]>([]);
  const [partyRows, setPartyRows] = useState<ResponsiblePartyRow[]>([]);
  const [drafts, setDrafts] = useState<Record<string, DraftState>>({});
  const [adminRemarkDrafts, setAdminRemarkDrafts] = useState<AdminRemarkDrafts>({});
  const [savingAdminRemarkId, setSavingAdminRemarkId] = useState<number | null>(null);
  const [adminRemarkEditor, setAdminRemarkEditor] = useState<AdminRemarkEditorState | null>(null);

  const loadMine = useCallback(async (showLoader = true) => {
    if (showLoader) setLoading(true);
    setError(null);
    try {
      const response = await api.getMyProjectVerifications();
      setMyRows(response.projects);
      const nextDrafts: Record<string, DraftState> = {};
      for (const row of response.projects) {
        nextDrafts[row.projectId] = draftFromRecord(
          row.myVerification,
          row.agentAccessFolderLink || ''
        );
      }
      setDrafts(nextDrafts);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load verifications');
    } finally {
      if (showLoader) setLoading(false);
    }
  }, []);


  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.getAllProjectVerifications();
      setAllRows(response.projects);
      const nextDrafts: AdminRemarkDrafts = {};
      for (const project of response.projects) {
        for (const verification of project.verifications) {
          nextDrafts[verification.id] = verification.adminRemark || '';
        }
      }
      setAdminRemarkDrafts(nextDrafts);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load verifications');
    } finally {
      setLoading(false);
    }
  }, []);


  const loadParties = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.getResponsibleParties();
      setPartyRows(response.parties);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load responsible parties');
    } finally {
      setLoading(false);
    }
  }, []);

  // The "All Projects" and "Project Leads" tabs are admin / project-dept only.
  const refreshActiveTab = useCallback(() => {
    if (tab === 'all' && isAdmin) loadAll();
    else if (tab === 'parties' && isAdmin) loadParties();
    else loadMine();
  }, [tab, isAdmin, loadMine, loadAll, loadParties]);

  useEffect(() => {
    if (open) setTab(initialTab === 'mine' || isAdmin ? initialTab : 'mine');
  }, [open, initialTab, isAdmin]);

  // Guard against landing on a restricted tab (e.g. via initialTab) without access.
  useEffect(() => {
    if ((tab === 'all' || tab === 'parties') && !isAdmin) setTab('mine');
  }, [tab, isAdmin]);

  useEffect(() => {
    if (!open) return;
    if (tab === 'all' && isAdmin) loadAll();
    else if (tab === 'parties' && isAdmin) loadParties();
    else loadMine();
  }, [open, tab, isAdmin, loadMine, loadAll, loadParties]);

  const updateDraft = (projectId: string, patch: Partial<DraftState>) => {
    setDrafts((prev) => ({
      ...prev,
      [projectId]: { ...(prev[projectId] || emptyDraft()), ...patch },
    }));
  };

  const saveVerification = async (
    projectId: string,
    draft: DraftState,
    showRefreshLoader = true
  ): Promise<{ ok: boolean; resourceLinkAlreadyExists: boolean }> => {
    setSubmittingProjectId(projectId);
    setError(null);
    try {
      const response = await api.submitProjectVerification({
        projectId,
        siteMapVerified: draft.siteMap,
        unitsVerified: draft.units,
        priceListVerified: draft.priceList,
        takenAction: draft.takenAction,
        projectEssentialVerified: draft.projectEssential,
        agentAccessFolderLink: draft.agentAccessFolderLink.trim() || null,
        remarks: draft.remarks.trim() || null,
      });
      await loadMine(showRefreshLoader);
      return { ok: true, resourceLinkAlreadyExists: Boolean(response.resourceLinkAlreadyExists) };
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit verification');
      return { ok: false, resourceLinkAlreadyExists: false };
    } finally {
      setSubmittingProjectId(null);
    }
  };

  const handleSubmit = async (projectId: string) => {
    await saveVerification(projectId, drafts[projectId] || emptyDraft());
  };

  const handleProjectEssentialConfirm = async (
    projectId: string,
    agentAccessFolderLink: string
  ): Promise<{ ok: boolean; resourceLinkAlreadyExists: boolean }> => {
    const draft = drafts[projectId] || emptyDraft();
    return saveVerification(
      projectId,
      {
        ...draft,
        projectEssential: true,
        agentAccessFolderLink: agentAccessFolderLink.trim(),
      },
      false
    );
  };

  const handleAdminRemarkChange = useCallback((verificationId: number, value: string) => {
    setAdminRemarkDrafts((prev) => ({ ...prev, [verificationId]: value }));
  }, []);

  const handleAdminRemarkSave = useCallback(async (verificationId: number) => {
    const draft = adminRemarkDrafts[verificationId] ?? '';
    setSavingAdminRemarkId(verificationId);
    setError(null);
    try {
      const response = await api.updateProjectVerificationAdminRemark(
        verificationId,
        draft.trim() || null
      );
      const updated = response.verification;
      setAllRows((prev) =>
        prev.map((project) => ({
          ...project,
          verifications: project.verifications.map((verification) =>
            verification.id === verificationId ? updated : verification
          ),
        }))
      );
      setAdminRemarkDrafts((prev) => ({ ...prev, [verificationId]: updated.adminRemark || '' }));
      setAdminRemarkEditor((prev) =>
        prev && prev.verificationId === verificationId
          ? { ...prev, originalRemark: updated.adminRemark || null }
          : prev
      );
      setAdminRemarkEditor((prev) => (prev?.verificationId === verificationId ? null : prev));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update admin remark');
    } finally {
      setSavingAdminRemarkId(null);
    }
  }, [adminRemarkDrafts]);

  const handleOpenAdminRemarkEditor = useCallback(
    (verificationId: number, projectName: string, verifierName: string, originalRemark: string | null) => {
      setAdminRemarkEditor({ verificationId, projectName, verifierName, originalRemark });
    },
    []
  );

  const handleCloseAdminRemarkEditor = useCallback(() => {
    if (savingAdminRemarkId !== null) return;
    setAdminRemarkEditor(null);
  }, [savingAdminRemarkId]);

  const tabs = useMemo(
    () =>
      isAdmin
        ? [
            { value: 'mine' as const, label: 'My Projects' },
            { value: 'all' as const, label: 'All Projects' },
            { value: 'parties' as const, label: 'Project Leads' },
          ]
        : [{ value: 'mine' as const, label: 'My Projects' }],
    [isAdmin]
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'var(--color-surface)',
          color: 'var(--color-text-strong)',
          minHeight: '70vh',
        },
      }}
    >
      <DialogTitle sx={{ borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
          <Box>
            <Typography variant="h6">Project Verification</Typography>
            <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.72)' }}>
              Confirm project information and marketing materials, including the Agent Access Folder.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <IconButton
              onClick={refreshActiveTab}
              size="small"
              disabled={loading}
              title="Refresh"
            >
              <RefreshIcon />
            </IconButton>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2.5 }}>
        {tabs.length > 1 && (
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
            {tabs.map((t) => (
              <Tab key={t.value} value={t.value} label={t.label} />
            ))}
          </Tabs>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : tab === 'all' && isAdmin ? (
          <AllProjectsTable
            rows={allRows}
            adminRemarkDrafts={adminRemarkDrafts}
            savingAdminRemarkId={savingAdminRemarkId}
            onOpenAdminRemarkEditor={handleOpenAdminRemarkEditor}
          />
        ) : tab === 'parties' && isAdmin ? (
          <ResponsiblePartiesTable rows={partyRows} />
        ) : (
          <MyProjectsTable
            rows={myRows}
            drafts={drafts}
            submittingProjectId={submittingProjectId}
            submissionError={error}
            onDraftChange={updateDraft}
            onSubmit={handleSubmit}
            onConfirmProjectEssential={handleProjectEssentialConfirm}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      <AdminRemarkEditorDialog
        editor={adminRemarkEditor}
        draft={adminRemarkEditor ? adminRemarkDrafts[adminRemarkEditor.verificationId] ?? '' : ''}
        savingAdminRemarkId={savingAdminRemarkId}
        onChange={handleAdminRemarkChange}
        onClose={handleCloseAdminRemarkEditor}
        onSave={handleAdminRemarkSave}
      />
    </Dialog>
  );
};

interface ResponsiblePartiesTableProps {
  rows: ResponsiblePartyRow[];
}

const ResponsiblePartiesTable: React.FC<ResponsiblePartiesTableProps> = ({ rows }) => {
  // Sort by Price Promo Activated — 'asc' puts not-activated (N) first (actionable), 'desc' Y first.
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const sortedRows = useMemo(() => {
    return [...rows].sort((a, b) => {
      const av = a.pricePromoActivated ? 1 : 0;
      const bv = b.pricePromoActivated ? 1 : 0;
      if (av !== bv) return order === 'asc' ? av - bv : bv - av;
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [rows, order]);

  if (rows.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)', py: 4, textAlign: 'center' }}>
        No project leads or ICs require verification.
      </Typography>
    );
  }
  return (
    <TableContainer sx={stickyTableContainerSx}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={stickyHeadCellSx}>Name</TableCell>
            <TableCell sx={stickyHeadCellSx}>CEA</TableCell>
            <TableCell sx={stickyHeadCellSx}>Mobile</TableCell>
            <TableCell sx={stickyHeadCellSx}>Projects Under Charge</TableCell>
            <TableCell sx={stickyHeadCellSx} align="center">
              <TableSortLabel
                active
                direction={order}
                onClick={() => setOrder((o) => (o === 'asc' ? 'desc' : 'asc'))}
              >
                Price Promo Activated?
              </TableSortLabel>
            </TableCell>
            <TableCell sx={stickyHeadCellSx}>Reason</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedRows.map((row) => (
            <TableRow key={row.userId} hover>
              <TableCell sx={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>{row.name || row.userId}</TableCell>
              <TableCell sx={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>{row.ceaNumber || '—'}</TableCell>
              <TableCell sx={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>{row.mobile || '—'}</TableCell>
              <TableCell sx={{ verticalAlign: 'top' }}>
                <Stack spacing={0.5}>
                  {row.projects.map((p) => (
                    <Box key={p.projectId} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <VerificationStatusIcon value={p.verified} size="small" />
                      <Typography variant="body2">{p.projectName || p.projectId}</Typography>
                    </Box>
                  ))}
                </Stack>
              </TableCell>
              <TableCell align="center" sx={{ verticalAlign: 'top' }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 700, color: row.pricePromoActivated ? 'success.main' : 'error.main' }}
                >
                  {row.pricePromoActivated ? 'Y' : 'N'}
                </Typography>
              </TableCell>
              <TableCell sx={{ verticalAlign: 'top', minWidth: 220 }}>
                <Typography
                  variant="body2"
                  sx={{ color: row.reason ? 'error.main' : 'rgba(var(--color-text-rgb), 0.5)' }}
                >
                  {row.reason || '—'}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

interface MyProjectsTableProps {
  rows: MyProjectVerificationRow[];
  drafts: Record<string, DraftState>;
  submittingProjectId: string | null;
  submissionError: string | null;
  onDraftChange: (projectId: string, patch: Partial<DraftState>) => void;
  onSubmit: (projectId: string) => void;
  onConfirmProjectEssential: (
    projectId: string,
    agentAccessFolderLink: string
  ) => Promise<{ ok: boolean; resourceLinkAlreadyExists: boolean }>;
}

const MyProjectsTable: React.FC<MyProjectsTableProps> = ({
  rows,
  drafts,
  submittingProjectId,
  submissionError,
  onDraftChange,
  onSubmit,
  onConfirmProjectEssential,
}) => {
  const [essentialEditor, setEssentialEditor] = useState<{
    projectId: string;
    projectName: string;
    folderLink: string;
  } | null>(null);
  const [essentialEditorResourceLinks, setEssentialEditorResourceLinks] = useState<ProjectResourceLink[]>([]);
  const [duplicateLinkNotice, setDuplicateLinkNotice] = useState(false);

  useEffect(() => {
    if (!essentialEditor) {
      setEssentialEditorResourceLinks([]);
      setDuplicateLinkNotice(false);
      return;
    }
    let cancelled = false;
    api
      .getProjectResourceLinks(essentialEditor.projectId)
      .then((response) => {
        if (!cancelled) setEssentialEditorResourceLinks(response.items || []);
      })
      .catch(() => {
        if (!cancelled) setEssentialEditorResourceLinks([]);
      });
    return () => {
      cancelled = true;
    };
  }, [essentialEditor?.projectId]);

  const openEssentialEditor = (row: MyProjectVerificationRow, draft: DraftState) => {
    setDuplicateLinkNotice(false);
    setEssentialEditor({
      projectId: row.projectId,
      projectName: row.projectName || row.projectId,
      folderLink: draft.agentAccessFolderLink,
    });
  };

  const confirmProjectEssential = async () => {
    if (!essentialEditor || !isValidAgentAccessFolderLink(essentialEditor.folderLink)) return;
    const folderLink = essentialEditor.folderLink.trim();
    const alreadyExists = isDuplicateProjectResourceLink(essentialEditorResourceLinks, folderLink);
    const result = await onConfirmProjectEssential(essentialEditor.projectId, folderLink);
    const duplicateDetected = alreadyExists || result.resourceLinkAlreadyExists;
    if (duplicateDetected) {
      setDuplicateLinkNotice(true);
    }
    if (result.ok && !duplicateDetected) {
      setEssentialEditor(null);
    }
  };

  if (rows.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)', py: 4, textAlign: 'center' }}>
        You are not assigned as Project Head or IC of any project requiring verification.
      </Typography>
    );
  }
  const editorHasValidFolderLink = essentialEditor
    ? isValidAgentAccessFolderLink(essentialEditor.folderLink)
    : false;
  const savingEssential = essentialEditor
    ? submittingProjectId === essentialEditor.projectId
    : false;

  return (
    <>
      <TableContainer sx={stickyTableContainerSx}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={stickyHeadCellSx}>Project</TableCell>
              <TableCell sx={stickyHeadCellSx}>My Role</TableCell>
              <TableCell sx={stickyHeadCellSx} align="center">
                Site Map
              </TableCell>
              <TableCell sx={stickyHeadCellSx} align="center">
                Units
              </TableCell>
              <TableCell sx={stickyHeadCellSx} align="center">
                Price List
              </TableCell>
              <TableCell sx={stickyHeadCellSx} align="center">
                Taken Action
              </TableCell>
              <TableCell sx={stickyHeadCellSx} align="center">
                Project Essentials
              </TableCell>
              <TableCell sx={stickyHeadCellSx}>Remarks</TableCell>
              <TableCell sx={stickyHeadCellSx}>Last Verified</TableCell>
              <TableCell sx={stickyHeadCellSx} align="right">
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
            const draft = drafts[row.projectId] || emptyDraft();
            const submitting = submittingProjectId === row.projectId;
            const hasValidFolderLink = isValidAgentAccessFolderLink(draft.agentAccessFolderLink);
            return (
              <React.Fragment key={row.projectId}>
                <TableRow>
                  <TableCell>{row.projectName || row.projectId}</TableCell>
                  <TableCell>{row.myRole || '—'}</TableCell>
                  <TableCell align="center">
                    <Checkbox
                      checked={draft.siteMap}
                      onChange={(e) => onDraftChange(row.projectId, { siteMap: e.target.checked })}
                      disabled={submitting}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Checkbox
                      checked={draft.units}
                      onChange={(e) => onDraftChange(row.projectId, { units: e.target.checked })}
                      disabled={submitting}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Checkbox
                      checked={draft.priceList}
                      onChange={(e) => onDraftChange(row.projectId, { priceList: e.target.checked })}
                      disabled={submitting}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Checkbox
                      checked={draft.takenAction}
                      onChange={(e) => onDraftChange(row.projectId, { takenAction: e.target.checked })}
                      disabled={submitting}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Checkbox
                      checked={draft.projectEssential && hasValidFolderLink}
                      onChange={() => openEssentialEditor(row, draft)}
                      disabled={submitting}
                      sx={{ '&.Mui-checked': { color: 'var(--color-success)' } }}
                      inputProps={{
                        'aria-label': `Verify Project Essentials for ${row.projectName || row.projectId}`,
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ minWidth: 180 }}>
                    <TextField
                      size="small"
                      fullWidth
                      value={draft.remarks}
                      onChange={(e) => onDraftChange(row.projectId, { remarks: e.target.value })}
                      placeholder="Optional"
                      disabled={submitting}
                    />
                  </TableCell>
                  <TableCell>{formatDateTime(row.myVerification?.verifiedAt || null)}</TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => onSubmit(row.projectId)}
                      disabled={submitting}
                    >
                      {submitting ? 'Saving…' : 'Submit'}
                    </Button>
                  </TableCell>
                </TableRow>
                {row.verifications.length > 0 && (
                  <TableRow>
                    <TableCell colSpan={10} sx={{ pt: 0, pb: 1.5, bgcolor: 'rgba(var(--color-text-rgb), 0.03)' }}>
                      <ProjectVerificationHistory records={row.verifications} />
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={Boolean(essentialEditor)}
        onClose={() => {
          if (!savingEssential) setEssentialEditor(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'var(--color-surface)',
            color: 'var(--color-text-strong)',
          },
        }}
      >
        <DialogTitle>Project Essentials Verification</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {submissionError ? <Alert severity="error">{submissionError}</Alert> : null}
            {duplicateLinkNotice ? <ResourceLinkDuplicateAlert /> : null}
            <Typography variant="body2">
              I confirm that I have reviewed the marketing materials under{' '}
              <strong>{essentialEditor?.projectName}</strong> and that they are correct.
            </Typography>
            <TextField
              autoFocus
              required
              fullWidth
              type="url"
              label="Agent Access Folder Link"
              placeholder="https://..."
              value={essentialEditor?.folderLink || ''}
              onChange={(event) => {
                setDuplicateLinkNotice(false);
                setEssentialEditor((current) =>
                  current ? { ...current, folderLink: event.target.value } : current
                );
              }}
              error={Boolean(essentialEditor?.folderLink.trim()) && !editorHasValidFolderLink}
              helperText={
                essentialEditor?.folderLink.trim() && !editorHasValidFolderLink
                  ? 'Link must start with http:// or https://'
                  : 'Required to complete Project Essentials verification'
              }
              disabled={savingEssential}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEssentialEditor(null)} disabled={savingEssential}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmProjectEssential}
            disabled={!editorHasValidFolderLink || savingEssential}
          >
            {savingEssential ? 'Saving…' : 'Confirm Verification'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const AllProjectsTable: React.FC<{
  rows: AllProjectVerificationRow[];
  adminRemarkDrafts: AdminRemarkDrafts;
  savingAdminRemarkId: number | null;
  onOpenAdminRemarkEditor: (
    verificationId: number,
    projectName: string,
    verifierName: string,
    originalRemark: string | null
  ) => void;
}> = ({ rows, adminRemarkDrafts, savingAdminRemarkId, onOpenAdminRemarkEditor }) => {
  const [orderBy, setOrderBy] = useState<AllProjectsSortKey | null>(null);
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');

  const handleRequestSort = (property: AllProjectsSortKey) => {
    if (orderBy === property) {
      setOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setOrderBy(property);
      setOrder('desc');
    }
  };

  const flatRows = useMemo(() => buildAllProjectsFlatRows(rows), [rows]);

  const sortedRows = useMemo(() => {
    if (!orderBy) return flatRows;
    const copy = [...flatRows];
    copy.sort((a, b) => compareAllProjectsFlatRows(a, b, orderBy, order));
    return copy;
  }, [flatRows, orderBy, order]);

  if (rows.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)', py: 4, textAlign: 'center' }}>
        No projects found.
      </Typography>
    );
  }

  const sortLabel = (id: AllProjectsSortKey, label: string) => (
    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
      <TableSortLabel
        active={orderBy === id}
        direction={orderBy === id ? order : 'asc'}
        onClick={() => handleRequestSort(id)}
        sx={{
          color: 'inherit',
          '&.MuiTableSortLabel-root.Mui-active': { color: 'inherit' },
          '& .MuiTableSortLabel-icon': { color: 'var(--color-accent) !important' },
        }}
      >
        {label}
      </TableSortLabel>
    </Box>
  );

  return (
    <TableContainer sx={stickyTableContainerSx}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={stickyHeadCellSx}>Project</TableCell>
            <TableCell sx={stickyHeadCellSx}>Verifier</TableCell>
            <TableCell sx={stickyHeadCellSx}>Role</TableCell>
            <TableCell sx={stickyHeadCellSx}>Special Promo</TableCell>
            <TableCell sx={stickyHeadCellSx} align="center">
              {sortLabel('siteMap', 'Site Map')}
            </TableCell>
            <TableCell sx={stickyHeadCellSx} align="center">
              {sortLabel('units', 'Units')}
            </TableCell>
            <TableCell sx={stickyHeadCellSx} align="center">
              {sortLabel('priceList', 'Price List')}
            </TableCell>
            <TableCell sx={stickyHeadCellSx} align="center">
              {sortLabel('takenAction', 'Taken Action')}
            </TableCell>
            <TableCell sx={{ ...stickyHeadCellSx, minWidth: 260 }} align="center">
              {sortLabel('projectEssential', 'Project Essentials')}
            </TableCell>
            <TableCell sx={stickyHeadCellSx}>Remarks</TableCell>
            <TableCell sx={stickyHeadCellSx}>Admin Remark</TableCell>
            <TableCell sx={stickyHeadCellSx}>
              <TableSortLabel
                active={orderBy === 'verifiedAt'}
                direction={orderBy === 'verifiedAt' ? order : 'asc'}
                onClick={() => handleRequestSort('verifiedAt')}
                sx={{
                  color: 'inherit',
                  '&.MuiTableSortLabel-root.Mui-active': { color: 'inherit' },
                  '& .MuiTableSortLabel-icon': { color: 'var(--color-accent) !important' },
                }}
              >
                Verified At
              </TableSortLabel>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedRows.map((row) =>
            row.kind === 'empty' ? (
              <TableRow key={`${row.project.projectId}-empty`}>
                <TableCell>{row.project.projectName || row.project.projectId}</TableCell>
                <TableCell sx={{ color: 'rgba(var(--color-text-rgb), 0.5)', fontStyle: 'italic' }}>—</TableCell><TableCell colSpan={10} sx={{ color: 'rgba(var(--color-text-rgb), 0.5)', fontStyle: 'italic' }}>
                  No verification submitted yet
                </TableCell>
              </TableRow>
            ) : (
              <TableRow key={`${row.project.projectId}-${row.verification.id}`}>
                <TableCell>{row.project.projectName || row.project.projectId}</TableCell>
                <TableCell>
                  {row.verification.userName || row.verification.ceaNumber || row.verification.userId}
                  {row.verification.ceaNumber ? (
                    <Typography variant="caption" sx={{ display: 'block', color: 'rgba(var(--color-text-rgb), 0.5)' }}>
                      {row.verification.ceaNumber}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell>{row.verification.role || '—'}</TableCell>
                <TableCell>
                  —
                </TableCell>
                <TableCell align="center">
                  <VerificationStatusIcon value={row.verification.siteMapVerified} />
                </TableCell>
                <TableCell align="center">
                  <VerificationStatusIcon value={row.verification.unitsVerified} />
                </TableCell>
                <TableCell align="center">
                  <VerificationStatusIcon value={row.verification.priceListVerified} />
                </TableCell>
                <TableCell align="center">
                  <VerificationStatusIcon value={row.verification.takenAction} />
                </TableCell>
                <TableCell align="center" sx={{ minWidth: 260, maxWidth: 360 }}>
                  <Stack spacing={0.5} alignItems="center">
                    <VerificationStatusIcon value={row.verification.projectEssentialVerified} />
                    {row.verification.projectEssentialVerified &&
                    (row.verification.agentAccessFolderLink || row.project.agentAccessFolderLink) ? (
                      <Link
                        href={
                          row.verification.agentAccessFolderLink ||
                          row.project.agentAccessFolderLink ||
                          undefined
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="caption"
                        sx={{ wordBreak: 'break-all', textAlign: 'center' }}
                      >
                        {row.verification.agentAccessFolderLink || row.project.agentAccessFolderLink}
                      </Link>
                    ) : null}
                  </Stack>
                </TableCell>
                <TableCell>{row.verification.remarks || '—'}</TableCell>
                <TableCell sx={{ minWidth: 260 }}>
                  <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                    <Typography
                      variant="body2"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        color: row.verification.adminRemark
                          ? 'var(--color-text-strong)'
                          : 'rgba(var(--color-text-rgb), 0.55)',
                        flex: 1,
                      }}
                    >
                      {row.verification.adminRemark || '—'}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() =>
                        onOpenAdminRemarkEditor(
                          row.verification.id,
                          row.project.projectName || row.project.projectId,
                          row.verification.userName || row.verification.ceaNumber || row.verification.userId,
                          row.verification.adminRemark
                        )
                      }
                      disabled={savingAdminRemarkId === row.verification.id}
                      title={row.verification.adminRemark ? 'Edit admin remark' : 'Add admin remark'}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </TableCell>
                <TableCell>{formatDateTime(row.verification.verifiedAt)}</TableCell>
              </TableRow>
            )
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const VerificationLines: React.FC<{ records: ProjectVerificationRecord[] }> = ({ records }) => (
  <Stack spacing={1.25}>
    {records.map((r) => (
      <Box key={r.id}>
        <Stack direction="row" spacing={0.75} alignItems="baseline" flexWrap="wrap" useFlexGap>
          <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.2 }}>
            {r.userName || r.ceaNumber || r.userId}
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}>
            {`${r.role || '—'} · ${formatDateTime(r.verifiedAt)}`}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
          {VERIFICATION_ITEMS.map((item) => (
            <StatusPill key={item.label} value={item.read(r)} label={item.label} />
          ))}
        </Stack>
        {r.agentAccessFolderLink ? (
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
            <FolderOpenIcon sx={{ fontSize: 14, color: 'rgba(var(--color-text-rgb), 0.5)' }} />
            <Link
              href={r.agentAccessFolderLink}
              target="_blank"
              rel="noopener noreferrer"
              variant="caption"
              sx={{ wordBreak: 'break-all' }}
            >
              Agent Access Folder
            </Link>
          </Stack>
        ) : null}
      </Box>
    ))}
  </Stack>
);

const ProjectVerificationHistory: React.FC<{ records: ProjectVerificationRecord[] }> = ({
  records,
}) => {
  const [open, setOpen] = useState(true);

  return (
    <Box>
      <Button
        size="small"
        onClick={() => setOpen((prev) => !prev)}
        endIcon={
          <ExpandMoreIcon
            sx={{
              transition: 'transform 150ms ease',
              transform: open ? 'rotate(180deg)' : 'none',
            }}
          />
        }
        sx={{
          px: 0.5,
          minWidth: 0,
          textTransform: 'none',
          fontWeight: 600,
          color: 'rgba(var(--color-text-rgb), 0.7)',
        }}
      >
        {`Latest verifications on this project (${records.length})`}
      </Button>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ pt: 0.5, pl: 0.5 }}>
          <VerificationLines records={records} />
        </Box>
      </Collapse>
    </Box>
  );
};

const AdminRemarkEditorDialog: React.FC<{
  editor: AdminRemarkEditorState | null;
  draft: string;
  savingAdminRemarkId: number | null;
  onChange: (verificationId: number, value: string) => void;
  onClose: () => void;
  onSave: (verificationId: number) => void;
}> = ({ editor, draft, savingAdminRemarkId, onChange, onClose, onSave }) => {
  if (!editor) return null;

  const saving = savingAdminRemarkId === editor.verificationId;
  const changed = normalizeOptionalText(draft) !== normalizeOptionalText(editor.originalRemark);

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'var(--color-surface)',
          color: 'var(--color-text-strong)',
        },
      }}
    >
      <DialogTitle>Edit Admin Remark</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ pt: 1 }}>
          <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.72)' }}>
            {editor.projectName}
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}>
            Verifier: {editor.verifierName}
          </Typography>
          <TextField
            multiline
            minRows={5}
            maxRows={12}
            fullWidth
            value={draft}
            onChange={(e) => onChange(editor.verificationId, e.target.value)}
            placeholder="Enter admin remark"
            disabled={saving}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => onSave(editor.verificationId)} disabled={saving || !changed}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProjectVerificationDialog;
